<!-- eslint-disable tailwindcss/classnames-order -->
<template>
  <div
    ref="scrollMask"
    class="
      min-h-24 overflow-hidden transition-all duration-75 ease-out
      lg:min-h-30
      print:!h-max
    "
  >
    <div
      ref="subHeaderContent"
      class="
        min-h-36 bg-cover py-6 text-white
        lg:min-h-49 lg:pb-10 lg:pt-8
      "
      :style="{ backgroundImage: `url(${data?.image.permalink})`, backgroundPosition: data?.image.focus_css}"
    >
      <Container v-if="showContent" class="px-6">
        <div class="flex items-center justify-between">
          <NuxtLink class="mb-6 block h-24" :to="localePath('/')">
            <h2
              v-if="!data?.is_logo"
              class="
                typo-h-1 mb-4 block text-white
                lg:mb-6
                max-sm:text-3xl
              "
            >
              {{ data?.heading }}
            </h2>
            <AppImage v-else class="h-full w-auto" :src="data?.logo" />
          </NuxtLink>
          <button
            class="
              mb-4
              lg:hidden
            "
            type="button"
            :aria-expanded="collapseHeight > 0"
            aria-controls="sub_header_collapse"
            :aria-label="collapseHeight > 0 ? $t('aria_label.text_hide') : $t('aria_label.text_show')"
            @click="toggleCollapse()"
          >
            <IconArrowDown
              class="size-5 transition-transform"
              :class="collapseHeight > 0 ? `-rotate-180 duration-150 ease-out` : `
                delay-50 duration-150 ease-in
              `"
            />
          </button>
        </div>
        <p
          id="sub_header_collapse"
          ref="collapseRef"
          :style="`height: ${collapseHeight}px;`"
          class="
            max-w-prose overflow-hidden text-base transition-[height,opacity]
          "
          :class="collapseHeight > 0 ? `
            opacity-100 duration-[200ms,500ms] ease-out
          ` : `opacity-0 duration-[150ms,100ms] ease-out`"
        >
          {{ data?.lead }}
          <AppButton
            v-if="data?.link"
            exact-active-class="!hidden"
            :to="data?.link"
            class="
              mb-2
              lg:ml-2 lg:inline-flex lg:py-1
              max-lg:mt-4
            "
            variant="secondary"
          >
            {{ data?.link_text }}
          </AppButton>
        </p>
      </Container>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  breakpointsTailwind, useBreakpoints,
} from '@vueuse/core'

const localePath = useLocalePath()
const globals = inject(GlobalsKey)

const data = computed(() => globals?.value.sub_header)
const showContent = computed(() => data.value?.heading || data.value?.logo || data.value?.lead || data.value?.link)

const breakpoints = useBreakpoints(breakpointsTailwind)
const lgAndLarger = breakpoints.greaterOrEqual('lg')

const isMounted = ref(false)
const collapseRef = ref<HTMLElement | undefined>(undefined)
const collapseHeight = ref<number>(0)

const scrollMask = ref<HTMLElement | null>(null)
const subHeaderContent = ref<HTMLElement | null>(null)

const { height: subHeaderHeight } = useElementBounding(subHeaderContent)

watch(lgAndLarger, () => {
  // when we enlarge the viewport past lg, we want to open the subheader
  if (lgAndLarger.value && collapseHeight.value === 0) {
    toggleCollapse()
  }

  // when we shrink the viewport to below lg, we want to close the subheader
  if (!lgAndLarger.value && collapseHeight.value > 0) {
    toggleCollapse()
  }
})

const handleScroll = () => {
  if (lgAndLarger.value && scrollMask.value) {
    scrollMask.value.style.height = `${subHeaderHeight.value - window.scrollY}px`
  }
}

async function toggleCollapse() {
  const scrollHeight = collapseRef.value?.scrollHeight
  if (scrollHeight) {
    collapseHeight.value = collapseHeight.value === 0 ? scrollHeight : 0
  }
}

onMounted(() => {
  if (lgAndLarger.value) {
    collapseHeight.value = collapseRef.value?.scrollHeight || 0
  }

  isMounted.value = true

  if (!scrollMask.value) return

  const onScroll = () => handleScroll()
  window.addEventListener('scroll', onScroll)

  onUnmounted(() => {
    window.removeEventListener('scroll', onScroll)
  })
})
</script>
